import React from 'react';
import ReactDOM from "react-dom";
import MaterialTable from 'material-table';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// MUI transitions
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';

export default class InsurancesDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
    this.theme = createMuiTheme({
      palette: {
        primary: {
          main: '#7cb342',
        },
        secondary: {
          main: '#4caf50',
        },
      },
    });

    this.state = {
      columns: [
        { title: 'Name', field: 'name' },
        { title: 'Description', field: 'description' },
        { title: 'Created at', field: 'created_at', editable: 'never' },
      ],
      data: [],
      page: null,
      open: false,
      totalCount: null,

      // Modal state
      modalOpen: false,
      selectedInsurance: null,

      // File-related state
      selectedFile: null,
      fileName: '',
      previewUrl: ''
    };
  }

  // =====================
  // Logo Modal Handlers
  // =====================
  handleOpenModal = (insurance) => {
    this.setState({
      modalOpen: true,
      selectedInsurance: insurance
    });
  };

  handleCloseModal = () => {
    this.setState({
      modalOpen: false,
      selectedInsurance: null,
      selectedFile: null,
      fileName: '',
      previewUrl: ''
    });
  };

  handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Create an object URL for preview
    const previewUrl = URL.createObjectURL(file);

    this.setState({
      selectedFile: file,
      fileName: file.name,
      previewUrl: previewUrl
    });
  };

  handleLogoUpdate = async () => {
    const { selectedFile, selectedInsurance } = this.state;
    if (!selectedFile || !selectedInsurance) return;

    const formData = new FormData();
    formData.append('logo', selectedFile);

    const csrfToken = document.querySelector('[name=csrf-token]').content;
    const url = `/api/insurances/${selectedInsurance[0].id}`;

    try {
      const response = await fetch(url, {
        method: 'PATCH', // or 'PUT'
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: formData
      });

      const result = await response.json();
      console.log('Logo update result:', result);

      if (response.ok) {
        this.handleCloseModal();
        if (this.tableRef.current) {
          this.tableRef.current.onQueryChange();
        }
      } else {
        console.error('Error updating logo:', result);
      }
    } catch (err) {
      console.error('Error updating logo:', err);
    }
  };

  // =====================
  // MaterialTable Queries
  // =====================
  fetchData(query) {
    return new Promise((resolve, reject) => {
      const requestedPage = query.page + 1;

      console.log('Query details:', {
        page: query.page,
        pageSize: query.pageSize,
        requestedPage: requestedPage,
        search: query.search
      });

      let url = '/api/insurances?';
      url += 'per_page=' + query.pageSize;
      url += '&q=' + query.search;
      url += '&page=' + requestedPage;

      fetch(url)
        .then(res => {
          if (res && res.status === 200 && res.ok) {
            return res.json();
          } else {
            console.log('Error response, fetching fallback data');
            const lastPageUrl = '/api/insurances?per_page=' + query.pageSize + '&q=' + query.search + '&page=1';
            return fetch(lastPageUrl)
              .then(fallbackRes => fallbackRes.json())
              .then(fallbackData => {
                const lastPage = fallbackData.pagination.last;
                console.log('Fallback - redirecting to last page:', lastPage);
                const correctedUrl = '/api/insurances?per_page=' + query.pageSize + '&q=' + query.search + '&page=' + lastPage;
                return fetch(correctedUrl).then(correctedRes => correctedRes.json());
              });
          }
        })
        .then(data => {
          console.log('Fetch response:', data);

          const currentPage = data.pagination.page - 1;
          const totalCount = data.pagination.count;
          const actualPageSize = parseInt(data.pagination.vars.items) || query.pageSize;
          const lastPage = data.pagination.last - 1;

          console.log('Pagination calculation:', {
            currentPage,
            totalCount,
            actualPageSize,
            lastPage,
            requestedPage: query.page,
            queryPageSize: query.pageSize
          });

          let adjustedTotalCount = totalCount;
          if (query.page > lastPage) {

            adjustedTotalCount = (lastPage + 1) * query.pageSize;
            console.log('Adjusted total count to prevent overflow:', adjustedTotalCount);
          }

          resolve({
            data: data.insurances,
            page: currentPage,
            totalCount: adjustedTotalCount
          });
        })
        .catch(error => {
          console.error('Fetch error:', error);
          reject(error);
        });
    });
  }

  batchDelete(insurances) {
    const array_keys = insurances.map(insurance => insurance.id);
    const csrfToken = document.querySelector('[name=csrf-token]').content;

    return new Promise((resolve, reject) => {
      fetch('/api/insurances/batch_delete', {
        method: 'POST',
        body: JSON.stringify({ insurance_ids: array_keys }),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          'X-CSRF-Token': csrfToken
        }
      })
      .then(response => response.json())
      .then(result => {
        console.log('Batch delete result:', result);
        if (this.tableRef.current) {
          this.tableRef.current.onQueryChange();
        }
        resolve();
      })
      .catch(err => reject(err));
    });
  }

  // =====================
  // Render Modal
  // =====================
  renderModal() {
    return (
      <Modal
        open={this.state.modalOpen}
        onClose={this.handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={this.state.modalOpen} timeout={500}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgcolor="#fff"
            p={4}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              outline: 'none',
              borderRadius: '4px',
              minWidth: '300px'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Update Insurance Logo
            </Typography>

            {/* MATERIAL-UI–STYLE FILE PICKER */}
            <Button
              variant="outlined"
              component="label"
              style={{ marginBottom: '1rem' }}
            >
              Choose File
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={this.handleFileSelect}
              />
            </Button>

            {/* Show the file name if present */}
            {this.state.fileName && (
              <Typography variant="body2" style={{ marginBottom: '1rem' }}>
                Selected: {this.state.fileName}
              </Typography>
            )}

            {/* Show the preview if we have a previewUrl */}
            {this.state.previewUrl && (
              <img
                src={this.state.previewUrl}
                alt="Logo Preview"
                style={{ width: '120px', height: 'auto', marginBottom: '1rem' }}
              />
            )}

            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleLogoUpdate}
                disabled={!this.state.selectedFile}
                style={{ marginRight: '0.5rem' }}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleCloseModal}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  }

  // =====================
  // Render Component
  // =====================
  render() {
    return (
      <MuiThemeProvider theme={this.theme}>
        {this.renderModal()}

        <MaterialTable
          title={this.props.title}
          columns={this.state.columns}
          tableRef={this.tableRef}
          actions={[
            {
              icon: 'refresh',
              tooltip: 'Refresh Data',
              isFreeAction: true,
              onClick: () =>
                this.tableRef.current && this.tableRef.current.onQueryChange(),
            },
            {
              tooltip: 'Remove All Selected',
              icon: 'delete',
              onClick: (evt, selectedRows) =>
                this.batchDelete(selectedRows).then(() => this.setState({ open: true }))
            },
            rowData => ({
              icon: 'image',
              tooltip: 'Update Logo',
              onClick: (evt, rowData) => this.handleOpenModal(rowData)
            })
          ]}
          options={{
            pageSize: 10,
            pageSizeOptions: [10, 20, 30, 50],
            exportButton: true,
            sorting: true,
            search: true,
            selection: true,
            addRowPosition: 'first',
            // Force MaterialTable to respect the pagination boundaries
            emptyRowsWhenPaging: false,
            // This will help ensure proper pagination behavior
            paginationType: 'normal'
          }}
          data={query =>
            this.fetchData(query).then(result => {
              // Update component state to track current pagination
              this.setState({
                currentPage: result.page,
                totalCount: result.totalCount
              });
              return result;
            })
          }
          editable={{
            onRowAdd: newData =>
              new Promise((resolve, reject) => {
                const csrfToken = document.querySelector('[name=csrf-token]').content;
                fetch('/api/insurances/', {
                  method: 'POST',
                  body: JSON.stringify(newData),
                  headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    'X-CSRF-Token': csrfToken
                  }
                })
                .then(response => response.json())
                .then(result => {
                  console.log('Create result:', result);
                  resolve();
                  if (this.tableRef.current) {
                    this.tableRef.current.onQueryChange();
                  }
                })
                .catch(err => reject(err));
              }),

            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                const csrfToken = document.querySelector('[name=csrf-token]').content;
                fetch(`/api/insurances/${newData.id}`, {
                  method: 'PUT',
                  body: JSON.stringify(newData),
                  headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    'X-CSRF-Token': csrfToken
                  }
                })
                .then(response => response.json())
                .then(result => {
                  console.log('Update result:', result);
                  resolve();
                  if (this.tableRef.current) {
                    this.tableRef.current.onQueryChange();
                  }
                })
                .catch(err => reject(err));
              }),

            onRowDelete: oldData =>
              new Promise((resolve, reject) => {
                const csrfToken = document.querySelector('[name=csrf-token]').content;
                fetch(`/api/insurances/${oldData.id}`, {
                  method: 'DELETE',
                  headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    'X-CSRF-Token': csrfToken
                  }
                })
                .then(response => response.json())
                .then(result => {
                  console.log('Delete result:', result);
                  resolve();
                  if (this.tableRef.current) {
                    this.tableRef.current.onQueryChange();
                  }
                })
                .catch(err => reject(err));
              }),
          }}
        />
      </MuiThemeProvider>
    );
  }
}